import { NavLink, useNavigate } from 'react-router-dom';
import { HomeIcon, LogOutIcon, StethoscopeIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { INSTITUTION } from '../data/proposalContent';

interface AppBarProps {
  showHome?: boolean;
}

export function AppBar({ showHome = true }: AppBarProps) {
  const { user, signOut, save, status } = usePortal();
  const navigate = useNavigate();

  const initials = (user?.fullName ?? 'R')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

  /* Someone can hold more than one role — an IRB officer who also reviews, for
     instance — so the bar offers every desk they are entitled to. */
  const desks = [
    { to: '/dashboard', label: 'My proposal', show: true },
    { to: '/committee', label: 'Committee', show: Boolean(user?.isSrcAdmin || user?.isIrbAdmin) },
    { to: '/reviewer', label: 'My reviews', show: Boolean(user?.isReviewer) }
  ].filter((d) => d.show);

  const handleSignOut = async () => {
    if (status?.canEdit) await save();
    await signOut();
    navigate('/');
  };

  return (
    <header className="appbar">
      <div className="container appbar-inner">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <StethoscopeIcon size={20} />
          </span>
          <span>
            <span className="brand-name">{INSTITUTION}</span>
            <span className="brand-sub">
              IRB / Research Advisory &amp; Grant Review Committee
            </span>
          </span>
        </div>

        {desks.length > 1 ? (
          <nav className="desk-nav" aria-label="Your desks">
            {desks.map((desk) => (
              <NavLink
                key={desk.to}
                to={desk.to}
                className={({ isActive }) => `desk-link${isActive ? ' desk-link--active' : ''}`}
              >
                {desk.label}
              </NavLink>
            ))}
          </nav>
        ) : null}

        <div className="appbar-actions">
          {user ? (
            <div className="user-chip">
              <span className="avatar" aria-hidden="true">
                {initials}
              </span>
              <span>
                <span className="user-chip-name">{user.fullName}</span>
                <br />
                <span className="user-chip-role">{user.designation}</span>
              </span>
            </div>
          ) : null}

          {showHome ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon size={16} aria-hidden="true" />
              Home
            </button>
          ) : null}

          <button type="button" className="btn btn--secondary" onClick={() => void handleSignOut()}>
            <LogOutIcon size={16} aria-hidden="true" />
            Log Out
          </button>
        </div>
      </div>
    </header>
  );
}
